/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ----------------------------------------------------------------
 * Desktop product gallery scroll-progress indicator
 * Updates the --progress CSS variable (0..1) on .product__gallery-progress
 * based on how far the user has scrolled through the stacked gallery.
 * ---------------------------------------------------------------- */
(function () {
  function updateGalleryProgress() {
    var gallery = document.querySelector('.product__desktop-gallery');
    if (!gallery) return;
    var progress = gallery.querySelector('.product__gallery-progress');
    if (!progress) return;

    var rect = gallery.getBoundingClientRect();
    var viewportH = window.innerHeight;
    var galleryH = rect.height;
    var maxScroll = galleryH - viewportH;

    var ratio = 0;
    if (maxScroll > 0) {
      var scrolled = -rect.top;
      ratio = Math.min(1, Math.max(0, scrolled / maxScroll));
    }
    progress.style.setProperty('--progress', ratio.toFixed(4));
  }

  function init() {
    if (!window.matchMedia('(min-width: 1000px)').matches) return;
    updateGalleryProgress();
    window.addEventListener('scroll', updateGalleryProgress, { passive: true });
    window.addEventListener('resize', updateGalleryProgress);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init after variant changes that re-render the product
  document.addEventListener('product:rerendered', updateGalleryProgress);
})();
/* ============================================================
   ZOLEADO v22 — cart quick-view + drawer interaction fixes
   ============================================================ */

/* A) IN-POPUP COLOR SWAP: Zoleado colors are separate PRODUCTS, so the color
   swatches inside Focal's quick-buy popup are plain links that would navigate
   away from the cart. Intercept them and reload the POPUP with the sibling
   color's content instead (same #quick-buy-content extraction Focal's own
   _load() does) — the shopper stays in the popup, Hondo-style.
   SEAMLESS: as soon as a quick-buy's content lands, every sibling color's page
   is pre-downloaded into an in-memory cache (5-min freshness, mirroring Focal's
   own variant cache) — so a color tap swaps in a frame with no network wait. */
(function () {
  var CACHE_TTL = 1000 * 60 * 5;
  var htmlCache = {}; /* url -> { t: fetchedAt, p: Promise<text> } */

  function cachedFetch(url) {
    var hit = htmlCache[url];
    if (hit && Date.now() - hit.t < CACHE_TTL) return hit.p;
    var p = fetch(url).then(function (r) { return r.text(); });
    htmlCache[url] = { t: Date.now(), p: p };
    return p;
  }

  /* Pre-download the sibling colors shown in this popup (staggered, cache-aware). */
  function warmSiblings(qb) {
    var links = qb.querySelectorAll('.sibling-color-swatch');
    Array.prototype.forEach.call(links, function (a, i) {
      var u = a.getAttribute('href');
      if (!u) return;
      var hit = htmlCache[u];
      if (hit && Date.now() - hit.t < CACHE_TTL) return;
      setTimeout(function () { cachedFetch(u); }, 200 + i * 150);
    });
  }

  /* Warm the moment a quick-buy opens and its content lands (load:end doesn't
     bubble, so hook the element directly when its toggle button is tapped). */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('button[is="toggle-button"][aria-controls]') : null;
    if (!btn) return;
    var qb = document.getElementById(btn.getAttribute('aria-controls'));
    if (!qb || (qb.tagName !== 'QUICK-BUY-DRAWER' && qb.tagName !== 'QUICK-BUY-POPOVER')) return;
    qb.addEventListener('openable-element:load:end', function () { warmSiblings(qb); }, { once: true });
    if (qb.querySelector('.sibling-color-swatch')) warmSiblings(qb); /* reopen: content already there */
  }, true);

  document.addEventListener('click', function (e) {
    var link = e.target.closest ? e.target.closest('quick-buy-drawer .sibling-color-swatch, quick-buy-popover .sibling-color-swatch') : null;
    if (!link) return;
    var qb = link.closest('quick-buy-drawer, quick-buy-popover');
    if (!qb) return;
    e.preventDefault();
    e.stopPropagation();
    var url = link.getAttribute('href');
    if (!url) return;
    qb.setAttribute('href', url); /* future reopens fetch this color too */
    var hit = htmlCache[url];
    var wasCached = hit && Date.now() - hit.t < CACHE_TTL;
    if (!wasCached) qb.classList.add('zol-qb-swapping'); /* veil only on a real network wait */
    cachedFetch(url).then(function (text) {
      var frag = document.createRange().createContextualFragment(text);
      var tpl = frag.querySelector('#quick-buy-content');
      var inner = tpl ? tpl.content.querySelector(qb.tagName.toLowerCase()) : null;
      if (!inner) { window.location.href = url; return; } /* graceful fallback: old behavior */
      qb.replaceChildren.apply(qb, Array.prototype.slice.call(inner.children));
      if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
      warmSiblings(qb); /* keep the whole color set warm from the new content too */
    }).catch(function () { window.location.href = url; })
      .then(function () { qb.classList.remove('zol-qb-swapping'); });
  }, true);
})();

/* B) POST-CLOSE CLICK SHIELD: closing the cart drawer can leak a ghost /
   synthesized click through to the product gallery underneath, popping open
   the image-zoom lightbox (a lone photo with an X — looks like the page
   broke). Eat gallery clicks for 450ms after the drawer closes; everything
   else stays clickable, and an intentional zoom a half-second later works. */
(function () {
  var suppressUntil = 0;
  function watch() {
    var d = document.querySelector('cart-drawer');
    if (!d) return;
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        if (m.attributeName === 'open' && !m.target.hasAttribute('open')) suppressUntil = Date.now() + 450;
      });
    }).observe(d, { attributes: true, attributeFilter: ['open'] });
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', watch); } else { watch(); }
  document.addEventListener('click', function (e) {
    if (Date.now() > suppressUntil) return;
    if (e.target.closest && e.target.closest('product-media, .product__media-list-wrapper, .product__desktop-gallery')) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);
})();
